import { useState } from 'react';
import { ModalStatusType } from '../../../types/common';
import { Form, Formik } from 'formik';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import FormSelect from '../../../components/dropdown/formSelect';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import _ from 'lodash';
import { useWealthlaneCustomerApi } from '../../../hooks';
import { ROLE_MAPPING_VALIDATION } from '../../../utils/validations/validations';
import useGetSourceCompanies from '../../../hooks/get/useGetSourceCompanies';
import useGetPublishedRolesFromSource from '../../../hooks/get/useGetPublishedRolesFromSource';
import useGetSelfRoles from '../../../hooks/get/useGetSelfRoles';
import usePostAllowedRoleMapping from '../../../hooks/post/usePostAllowedRoleMapping';
import usePutAllowedRoleMapping from '../../../hooks/put/usePutAllowedRoleMapping';

interface RoleMappingModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  modalLoading?: boolean;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  id?: string;
}

const RoleMappingModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  initialData,
  modalLoading,
  setModalStatus,
  id
}: RoleMappingModalType): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const [sourceId, setSourceId] = useState<string>(initialData.data?.sourceCompanyId ?? null);

  const { data: sourceCompaniesOption, isLoading: isSourceCompaniesLoading } =
    useGetSourceCompanies(handleRequest);
  const { data: publishedRolesOption, isLoading: isPublishedRolesLoading } =
    useGetPublishedRolesFromSource(handleRequest, sourceId);
  const { data: selfRolesOption, isLoading: isSelfRolesLoading } = useGetSelfRoles(handleRequest);

  const { mutate: addAllowedRoleMapping, isLoading: isRoleMapAdding } = usePostAllowedRoleMapping(
    handleRequest,
    closeModal
  );
  const { mutate: updateAllowedRoleMapping, isLoading: isRoleMapUpdating } =
    usePutAllowedRoleMapping(handleRequest, closeModal);

  const isLoading =
    modalLoading ||
    isSourceCompaniesLoading ||
    isPublishedRolesLoading ||
    isSelfRolesLoading ||
    isRoleMapUpdating ||
    isRoleMapAdding;

  const handleSubmit = (values: any): void => {
    if (modalStatus === 'Edit') {
      updateAllowedRoleMapping({
        id: initialData.id,
        payload: values,
      });
    } else {
      addAllowedRoleMapping(values);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialData.data}
      validationSchema={ROLE_MAPPING_VALIDATION}
      onSubmit={handleSubmit}>
      {({ touched, handleChange, errors, setFieldValue, setFieldTouched, values }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            // loading={loading || loader}
            size='sm'
            id={id}
            title={`${modalStatus === 'Add' ? 'Create' : modalStatus} Role Mapping`}
            valueEdited={_.isEqual(values, initialData.data)}
            children={
              <Form id="roleMappingForm">
                <ModalBodyComponent id="roleMappingModalBody">
                  <div className='' id="roleMappingFormContainer">
                    <FormSelect
                      type='text'
                      label={'Source Company'}
                      name='sourceCompanyId'
                      id='sourceCompanyId'
                      containerClassName='formGroup'
                      onChange={(value: string) => {
                        setFieldValue('sourceCompanyId', value ?? null);
                        setSourceId(value ?? null);
                      }}
                      errors={modalStatus === 'View' ? {} : errors}
                      touched={touched}
                      options={sourceCompaniesOption}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      isLoading={isSourceCompaniesLoading}
                      isClearable
                      required
                    />
                    {sourceId && (
                      <>
                        <FormSelect
                          type='text'
                          label={'Published Role'}
                          name='sourceRoleId'
                          id='sourceRoleId'
                          containerClassName='formGroup'
                          onChange={(value: string) => {
                            setFieldValue('sourceRoleId', value ?? null);
                          }}
                          errors={modalStatus === 'View' ? {} : errors}
                          touched={touched}
                          options={publishedRolesOption}
                          values={values}
                          setFieldValue={setFieldValue}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          isLoading={isPublishedRolesLoading}
                          isClearable
                          required
                        />
                        <FormSelect
                          type='text'
                          label={'Mapped Role'}
                          name='destinationRoleId'
                          id='destinationRoleId'
                          containerClassName='formGroup'
                          onChange={(value: string) => {
                            setFieldValue('destinationRoleId', value ?? null);
                          }}
                          errors={modalStatus === 'View' ? {} : errors}
                          touched={touched}
                          options={selfRolesOption ?? []}
                          values={values}
                          setFieldValue={setFieldValue}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          isClearable
                          required
                        />
                      </>
                    )}
                  </div>
                </ModalBodyComponent>
                <ModalFooterComponent
                  id="roleMappingModalFooter"
                  modalStatus={modalStatus}
                  loading={isLoading}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  valueChanged={_.isEqual(values, initialData.data)}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default RoleMappingModal;
